import { AppButton, AppInput, AppTextarea } from 'components/form';
import { AppDialog, AppSectionErrorBoundary, AppSpacer } from 'components/ui';
import AppLoader from 'components/ui/AppLoader';
import { Select } from '@trackman/web-shared-components';
import { useStores } from 'index';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IUserDiagnostic } from 'types/models/UserDiagnosticsModel';
import { TrackingDiagnosticsTag } from 'types/models/TrackingDiagnosticsModel';
import { parseMeasurementBody } from 'utilities/ParsingHelper';

const FTMF_TYPE_OPTIONS = Object.keys(TrackingDiagnosticsTag)
    .filter(key => isNaN(Number(key)) && key !== 'None')
    .map(key => ({ value: key, label: key }));

interface IRequestFtmfDialogProps {
    isOpened: boolean;
    onClose: () => void;
    userDiagnostic: IUserDiagnostic | null;
}

export const RequestFtmfDialog = observer(({ isOpened, onClose, userDiagnostic }: IRequestFtmfDialogProps) => {
    const { t } = useTranslation();
    const { trackingDiagnosticsStore } = useStores();

    const [type, setType] = useState(FTMF_TYPE_OPTIONS[0].value);
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (isOpened && userDiagnostic) {
            setType(FTMF_TYPE_OPTIONS[0].value);
            setDescription(userDiagnostic.userSubject
                ? `${userDiagnostic.userSubject}\n\n${userDiagnostic.userDescription ?? ''}`
                : userDiagnostic.userDescription ?? '');
        }
    }, [isOpened, userDiagnostic]);

    const measurementContext = useMemo(
        () => parseMeasurementBody(userDiagnostic?.rawInfoContext),
        [userDiagnostic?.rawInfoContext]
    );

    const onSubmit = useCallback(async () => {
        if (!userDiagnostic || !measurementContext.sessionId || !measurementContext.measurementId || !measurementContext.calibrationId) {
            return;
        }

        const success = await trackingDiagnosticsStore.requestFtmf({
            requesterId: userDiagnostic.operatorId,
            organizationId: userDiagnostic.organizationId,
            sessionId: measurementContext.sessionId,
            type,
            description,
            entries: [{
                measurementId: measurementContext.measurementId,
                calibrationId: measurementContext.calibrationId,
            }],
        });

        if (success) {
            onClose();
        }
    }, [userDiagnostic, measurementContext, type, description, trackingDiagnosticsStore, onClose]);

    const canSubmit = !!(measurementContext.sessionId && measurementContext.measurementId && measurementContext.calibrationId) && description.trim().length > 0;

    return (
        <AppDialog
            title={t('TrackingDiagnostics.RequestFtmf.Title')}
            isOpened={isOpened}
            onClose={onClose}
            maxWidth="md"
        >
            <div className='flex-auto d-flex flex-column justify-content-between'>
                <AppSectionErrorBoundary>
                    <div>
                        <section className='container'>
                            <AppSpacer space='15px' />

                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                {t('TrackingDiagnostics.RequestFtmf.Section.MeasurementData')}
                            </h3>
                            <AppSpacer space='10px' />
                            <AppInput
                                label={t('TrackingDiagnostics.Column.SessionId')}
                                name='sessionId'
                                value={measurementContext.sessionId ?? t('TrackingDiagnostics.RequestFtmf.NotAvailable')}
                                disabled
                            />
                            <AppSpacer space='10px' />
                            <AppInput
                                label={t('TrackingDiagnostics.Column.MeasurementId')}
                                name='measurementId'
                                value={measurementContext.measurementId ?? t('TrackingDiagnostics.RequestFtmf.NotAvailable')}
                                disabled
                            />
                            <AppSpacer space='10px' />
                            <AppInput
                                label={t('TrackingDiagnostics.Column.CalibrationId')}
                                name='calibrationId'
                                value={measurementContext.calibrationId ?? t('TrackingDiagnostics.RequestFtmf.NotAvailable')}
                                disabled
                            />

                            <AppSpacer space='20px' />
                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                {t('TrackingDiagnostics.RequestFtmf.Section.RequestDetails')}
                            </h3>
                            <AppSpacer space='10px' />

                            <Select
                                label={t('TrackingDiagnostics.Column.Type')}
                                notched={false}
                                value={type}
                                onChange={e => setType((e.target as HTMLSelectElement).value)}
                                selectOptions={FTMF_TYPE_OPTIONS}
                            />
                            <AppSpacer space='10px' />

                            <AppTextarea
                                label={t('TrackingDiagnostics.Column.Description')}
                                name='description'
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </section>
                        <AppSpacer space='20px' />
                    </div>
                    <footer className='d-flex align-items-center justify-content-between p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
                        <AppButton variant='secondary' onClick={onClose}>
                            {t('cancel')}
                        </AppButton>
                        <AppLoader isShowingLoader={trackingDiagnosticsStore.isRequestingFtmf}>
                            <AppButton
                                variant='primary'
                                onClick={onSubmit}
                                disabled={!canSubmit || trackingDiagnosticsStore.isRequestingFtmf}
                            >
                                {t('TrackingDiagnostics.RequestFtmf.Submit')}
                            </AppButton>
                        </AppLoader>
                    </footer>
                </AppSectionErrorBoundary>
            </div>
        </AppDialog>
    );
});
